import { INLET_MANIFOLD_ID, OUTLET_MANIFOLD_ID } from "../types";
import {
    ManifoldUtils,
} from "../manifolds/utils";
import store from "../../store";

/**
 * Specific shared services for Chambers
 * Functions should be kept clean and functional, avoiding side effects or data mutation
 * @type {{getMappedChambers: (function(*): Map<any, any>), getChamberByPosition: (function(*, *, *): *)}}
 */
const ChamberService = {

    ////////////// FUNCTIONS USED ON MANIFOLD ACTIONS TO CREATE METADATA
    /**
     * Determines position Up or Down, and if it is inner chamber
     * @param chamber
     * @param chambersLength
     * @param chamberIdx
     * @returns {{isUpper: boolean, isInnerChamber: boolean, type: string}}
     */
    isChamberType: (chamber, chambersLength, chamberIdx) => {
        if (
            chamber &&
            chamber.upperCapActive &&
            chamber.upperCapAccessory
        ) {
            if (
                chamber.upperCapAccessory.id === INLET_MANIFOLD_ID ||
                chamber.upperCapAccessory.id === OUTLET_MANIFOLD_ID
            ) {
                return {
                    isUpper: true,
                    type: "upper",
                    isInnerChamber: ( chamberIdx >= 1 && chamberIdx < chambersLength - 1 ),
                };
            }
        }
        if (
            chamber &&
            chamber.lowerCapActive &&
            chamber.lowerCapAccessory
        ) {
            if (
                chamber.lowerCapAccessory.id === INLET_MANIFOLD_ID ||
                chamber.lowerCapAccessory.id === OUTLET_MANIFOLD_ID
            ) {
                return {
                    isUpper: false,
                    type: "lower",
                    isInnerChamber: ( chamberIdx >= 1 && chamberIdx < chambersLength - 1 ),
                };
            }
        }
    },

    /**
     * Generates Manifold metadata used to build manifolds structures
     * @param manifoldType
     * @param isUpperManifold
     * @param chamber
     * @param existingManifolds
     * @param chamberType
     * @param totalStorageProvided
     * @param storageVolume
     * @param chamberRow
     * @param forceManifoldSpacer
     * @param isGeneratingSystem
     * @param getIsRowOffset
     * @param isInnerChamber
     * @returns {{forceManifoldSpacer: *, isRowOffset: boolean, id: *, manifoldSize: {}|null, letColumn: *, letRow: *, isInlet: boolean}}
     */
    processManifoldOnChamber: (
        manifoldType,
        isUpperManifold,
        chamber,
        existingManifolds,
        chamberType,
        totalStorageProvided,
        storageVolume,
        chamberRow,
        forceManifoldSpacer,
        isGeneratingSystem,
        getIsRowOffset,
        isInnerChamber = false
    ) => {
        const chamberAccessory = chamber[(isUpperManifold) ? 'upperCapAccessory' : 'lowerCapAccessory'];
        const isInlet = chamberAccessory.id === INLET_MANIFOLD_ID;

        const existingManifold = existingManifolds.manifolds
            .find(manifold => manifold.id === chamberAccessory.manifoldId);

        const manifoldConstruct = ManifoldUtils.getManifoldConstruct(chamberType.id, chamberAccessory.id, totalStorageProvided || storageVolume, isInnerChamber);

        let chamberTypeChanged = false;

        if (
            existingManifold &&
            existingManifold.manifoldSize &&
            existingManifold.manifoldSize.ChamberTypeId !== chamberType.id
        ) {
            chamberTypeChanged = true;
            existingManifold.manifoldSize.size = manifoldConstruct.sizing.manifoldSizeName;
        }

        let addManifold = {
            id: chamberAccessory.manifoldId,
            letRow: chamberRow.row,
            letColumn: chamber.col,
            isInlet,
            isRowOffset: false,
            //forceManifoldSpacer: (forceManifoldSpacer || manifoldConstruct.sizing.defaultManifoldSize.forceManifoldSpacer),
            forceManifoldSpacer,
            manifoldSize: manifoldConstruct.sizing.defaultManifoldSize
        };

        if (existingManifold) {
            addManifold.direction = existingManifold.direction;
            addManifold.stubsNumber = existingManifold.stubsNumber;
            addManifold.spacer = existingManifold.spacer;
            addManifold.isElevated = existingManifold.isElevated;
            addManifold.hasWeir = existingManifold.hasWeir;
            addManifold.userModified = true;
            addManifold.manifoldType = manifoldType;
            addManifold.hasSecondPipe = existingManifold.hasSecondPipe;
            addManifold.hasIsolator = existingManifold.hasIsolator;
            addManifold.refreshIsolator = existingManifold.refreshIsolator;
            addManifold.mSize = existingManifold.mSize;

            const fallbackSize = ManifoldUtils.getManifoldDatasetObject(manifoldConstruct.sizing.potentialSizes, existingManifold.manifoldSize.size);
            addManifold.manifoldSize =
                chamberTypeChanged ||
                !existingManifold.manifoldSize ||
                isGeneratingSystem ||
                !addManifold.userModified ? manifoldConstruct.sizing.defaultManifoldSize : fallbackSize || existingManifold.manifoldSize;
        } else {
            addManifold.isElevated = manifoldConstruct.manifoldPosition === "high";
            addManifold.hasWeir = manifoldConstruct.manifoldPosition !== "high";
            addManifold.manifoldType = manifoldType;
        }

        let structureSize = addManifold.manifoldSize[manifoldConstruct.structureType === "nyloplast" ? "nyloplastStructureSize" : "concreteStructureSize"];

        structureSize = !structureSize
            ? 48
            : typeof structureSize === "string"
                ? parseInt(structureSize, 10)
                : structureSize;

        addManifold.manifoldOffset = getIsRowOffset(manifoldConstruct.structureType, structureSize);

        return addManifold;
    },

    ////////////// CHAMBERS MAPS FUNCTIONS
    /**
     * returns a Map structure from chamberRowsArray
     *
     * @example
     * const mappedChamberSet = ChamberService.getMappedChambers(chamberRowsArray);
     *
     * @param chamberRowsArray
     * @returns {Map<any, any>}
     */
    getMappedChambers: (chamberRowsArray) => {
        let chambersMap = new Map();
        chamberRowsArray.forEach((chamberRow, rowIdx) => {
            let chambers = chamberRow.chambers;
            let chamberMap = new Map();
            chambers.forEach((chamber, chamberIdx) => {
                chamber['isInnerChamber'] = (chamberIdx >= 1 && chamberIdx < chambers.length - 1);
                chamberMap.set(chamberIdx, chamber);
            });
            chambersMap.set(rowIdx, chamberMap);
        });
        return chambersMap;
    },

    /**
     * Request chamber by Col and Row coordinates
     *
     * @example
     * ChamberService.getChamberByPosition(2, 4, mappedChamberSet);
     *
     * @param col
     * @param row
     * @param map
     * @returns {*}
     */
    getChamberByPosition: ( col, row, map ) => {
        const column = map.get(2); // access column
        return column.get(4); // access chamber
    },


    /**
     * Adds the manifold id to the isManifoldConnectedArray property of the chamber where the manifold or the stub
     * is connected, or changes the value to true,false on isOppositeStubAttached
     * @param type {string | null} - set the type used for the metadata
     * Options: isManifoldConnectedArray | isVoidArray
     * @param voidChambersArray
     * @param {number} row - the root row of the manifold
     * @param col - the root col of the manifold
     * @param props - Main props, requires modifyChambersByManifoldVoidArray action to be passed on mapDispatchToProps
     */
    setChamberManifoldIdMeta: (type = null, voidChambersArray, row, col, props) => {
        try {
            if (type !== null) {
                const state = store.getState();
                const chamber = state.chamberbed.chamberRows[row]._chambers[col];
                const manifoldId = ( chamber._upperCapAccessory ) ? chamber._upperCapAccessory.manifoldId : chamber._lowerCapAccessory.manifoldId;
                const objPayload = {};
                objPayload[type] = type !== "isOppositeStubAttached" ? { manifoldId: manifoldId } : { value: voidChambersArray[0].value };

                props.modifyChambersByManifoldVoidArray(
                    voidChambersArray,
                    row,
                    0,
                    objPayload,
                );
            } else {
                console.info('Please set type');
            }
        } catch (exception) {
            console.error(`ChamberService.setChamberConnectedManifold() :: ${exception.message}`);
            console.warn(`On Manifold ID error, Manifold was probably removed by other process`);
        }
    },

    /**
     * Checks if chamber is used by a manifold on void or connections
     * @param row {number}
     * @param col {number}
     * @param type {string | null} - set the type used for the metadata
     * Options: isManifoldConnectedArray | isVoidArray
     * @returns {boolean}
     */
    isChamberMetaInUse: (row, col, type = 'isVoidArray') => {
        const state = store.getState();
        const chamber = state.chamberbed.chamberRows[row]._chambers[col];
        return (chamber['_' + type].length > 0);
    },

};

export default ChamberService;



// WEBPACK FOOTER //
// ./src/lib/services/chambers-service.js